import React, { useEffect, useState } from "react";
import { Button, Checkbox, Select, ShieldTick, TextInput, TileGroup } from "@opendate-app/setlist";

import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import NodeErrorContainer from "../NodeErrorContainer";
import TemplateHTMLPreview from "../../BeefreeTemplates/TemplateHTMLPreview";
import SegmentPicker from "../../../../shared/SegmentPicker";
import SegmentAudienceWarningModal from "./SegmentAudienceWarningModal";

const ASSIGNED_STAFF = "Contact's assigned staff member";
const ASSIGNED_STAFF_LABEL = "Contact's relationship manager";

const FAN_ACTION_RECIPIENTS = [
  { value: "", label: "Fan", supportingText: "Send to the fan who performed the action" },
  { value: ASSIGNED_STAFF, label: ASSIGNED_STAFF_LABEL, supportingText: "Email the relationship manager assigned to this contact" },
];

const EVENT_RECIPIENTS = [
  { value: "Fan Segment", label: "Fan Segment", supportingText: "Send to an automated segment as defined by the trigger" },
  { value: "Ad Hoc", label: "Ad Hoc", supportingText: "Manually enter your email recipient(s)" },
];

const sectionHeaderStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const sectionTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: "var(--sl-text-primary)",
};

const fieldLabelStyle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "20px",
  color: "var(--sl-text-secondary)",
};

const helpTextStyle = {
  fontSize: 14,
  lineHeight: "20px",
  color: "var(--sl-text-tertiary)",
};

const addContentContainerStyle = {
  background: "var(--sl-bg-tertiary)",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  borderRadius: 4,
  border: "1px dashed var(--sl-border-primary)",
};

const previewContainerStyle = {
  borderRadius: 4,
  border: "1px dashed var(--sl-border-primary)",
  minWidth: 400,
  overflow: "auto",
  marginBottom: 16,
  position: "relative",
  background: "#fff",
};

const htmlContentStyle = {
  border: "none",
  height: "100%",
  overflow: "hidden",
  width: "100%",
};

const floatingButtonContainerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "color-mix(in srgb, var(--sl-bg-tertiary) 70%, transparent)",
  zIndex: 10,
};

const recipientConfigurationStyle = {
  background: "var(--sl-bg-tertiary)",
  borderRadius: 8,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const hygieneContainerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  background: "var(--sl-bg-secondary)",
  borderRadius: 8,
  border: "1px solid var(--sl-border-secondary)",
};

const EmailNodeForm = () => {
  const {
    team,
    customCampaign,
    selectedNode,
    setShowBeefreeComposer,
    selectedNodeFormState,
    setSelectedNodeFormState,
  } = useCustomCampaignShowContext();

  const buildNodeDataFormState = () => {
    return {
      subscription_preference: selectedNode.node_data.subscription_preference || "",
      recipient_type: selectedNode.node_data.recipient_type || "",
      to: selectedNode.node_data.to || "",
      segment_name: selectedNode.node_data.segment_name || "",
      segment_id: selectedNode.node_data.segment_id || null,
      segment_label: selectedNode.node_data.segment_label || "",
      target_engaged_audience: selectedNode.node_data.target_engaged_audience || false,
    };
  };

  const [nodeDataFormState, setNodeDataFormState] = useState(buildNodeDataFormState());
  const [pendingSegment, setPendingSegment] = useState(null);

  useEffect(() => {
    setNodeDataFormState(buildNodeDataFormState());
  }, [selectedNode]);

  useEffect(() => {
    const debouncedUpdateNodeDataFormState = _.debounce(() => {
      selectedNodeFormState && setSelectedNodeFormState({
        ...selectedNodeFormState,
        node_data: { ...selectedNodeFormState.node_data, ...nodeDataFormState },
      });
    }, 300);

    debouncedUpdateNodeDataFormState();

    return () => {
      debouncedUpdateNodeDataFormState.cancel();
    };
  }, [nodeDataFormState]);

  const updateNodeDataFormState = (newNodeData) => {
    setNodeDataFormState({ ...nodeDataFormState, ...newNodeData });
  };

  const enableFanSegment = () => {
    if (nodeDataFormState.recipient_type === "Fan Segment") {
      return;
    }

    updateNodeDataFormState({
      recipient_type: "Fan Segment",
      to: "{{fan.email}}",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Use fan marketing preferences",
    });
  };

  const setFanActionRecipient = (recipientType) => {
    if ((nodeDataFormState.recipient_type || "") === recipientType) {
      return;
    }

    updateNodeDataFormState({
      recipient_type: recipientType,
      to: "{{fan.email}}",
      subscription_preference: recipientType === ASSIGNED_STAFF ? "Ignore fan marketing preferences" : "Use fan marketing preferences",
    });
  };

  const enableAdHoc = () => {
    if (nodeDataFormState.recipient_type === "Ad Hoc") {
      return;
    }

    updateNodeDataFormState({
      recipient_type: "Ad Hoc",
      to: "",
      segment_name: "",
      segment_id: null,
      segment_label: "",
      subscription_preference: "Ignore fan marketing preferences",
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={sectionHeaderStyle}>
        <div style={sectionTitleStyle}>Content</div>
        <NodeErrorContainer errors={[
          ...(selectedNodeFormState?.workflow_errors.content || []),
          ...(selectedNodeFormState?.workflow_errors.to || []),
          ...(selectedNodeFormState?.workflow_errors.email_send_domain || []),
          ...(selectedNodeFormState?.workflow_errors.subject || [])
        ]} />
      </div>
      {
        selectedNode.beefree_template ? (
          <div style={previewContainerStyle}>
            <TemplateHTMLPreview
              htmlContent={selectedNode.beefree_template.template_html}
              style={htmlContentStyle}
            />
            <div style={floatingButtonContainerStyle}>
              <Button size="sm" onClick={() => setShowBeefreeComposer(true)}>Edit Content</Button>
            </div>
          </div>
        ) : (
          <div style={addContentContainerStyle}>
            <Button size="sm" onClick={() => setShowBeefreeComposer(true)}>Add Content</Button>
          </div>
        )
      }
      {customCampaign.trigger_type === "Fan action" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={sectionHeaderStyle}>
            <div style={sectionTitleStyle}>Recipient</div>
            <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.recipient_type} />
          </div>
          <TileGroup
            aria-label="Recipient"
            columns={1}
            items={FAN_ACTION_RECIPIENTS}
            value={nodeDataFormState.recipient_type || ""}
            onChange={setFanActionRecipient}
          />
        </div>
      ) : null}
      {
        customCampaign.trigger_type === "Event" || customCampaign.trigger_type === "System Event" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={sectionHeaderStyle}>
              <div style={sectionTitleStyle}>Recipient</div>
              <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.recipient_type} />
            </div>
            <div style={helpTextStyle}>Event based triggers require recipient to be defined.</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <TileGroup
                aria-label="Recipient"
                columns={1}
                items={EVENT_RECIPIENTS}
                value={nodeDataFormState.recipient_type || null}
                onChange={(recipientType) => (recipientType === "Fan Segment" ? enableFanSegment() : enableAdHoc())}
              />
              {
                nodeDataFormState.recipient_type === "Fan Segment" ? (
                  <div style={recipientConfigurationStyle}>
                    <div style={sectionHeaderStyle}>
                      <div style={fieldLabelStyle}>Segment *</div>
                      <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.segment_name} />
                    </div>
                    <SegmentPicker
                      team={team}
                      value={{ segmentId: nodeDataFormState.segment_id, segmentName: nodeDataFormState.segment_name }}
                      selectedLabel={
                        (nodeDataFormState.segment_id && selectedNodeFormState?.node_data_options?.saved_segment_label) ||
                        nodeDataFormState.segment_label ||
                        nodeDataFormState.segment_name
                      }
                      relativeGroup={{
                        label: "Triggering event",
                        options: (selectedNodeFormState?.node_data_options?.relative_audience_names || []).map((name) => ({
                          value: name,
                          label: name,
                        })),
                      }}
                      onChange={(next) => {
                        if (next.segmentName) {
                          updateNodeDataFormState({ segment_name: next.segmentName, segment_id: null, segment_label: next.segmentName });
                        } else {
                          setPendingSegment(next);
                        }
                      }}
                    />
                  </div>
                ) : null
              }
              {
                nodeDataFormState.recipient_type === "Ad Hoc" ? (
                  <div style={recipientConfigurationStyle}>
                    <div style={sectionHeaderStyle}>
                      <div style={fieldLabelStyle}>Email recipients</div>
                      <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.to} />
                    </div>
                    <TextInput
                      aria-label="Email recipients"
                      value={nodeDataFormState.to}
                      onChange={(e) => updateNodeDataFormState({ to: e.target.value })}
                      placeholder="email@example.com, name@email.com, etc..."
                    />
                  </div>
                ) : null
              }
            </div>
          </div>
        ) : null
      }
      { nodeDataFormState.recipient_type !== "Ad Hoc" && nodeDataFormState.recipient_type !== ASSIGNED_STAFF ? (
        <>
          <div style={sectionHeaderStyle}>
            <div style={sectionTitleStyle}>Settings</div>
            <NodeErrorContainer errors={selectedNodeFormState?.workflow_errors?.settings} />
          </div>
          <Select
            label="Subscription preference"
            placeholder="Select fan subscription preference"
            options={(selectedNodeFormState?.node_data_options?.subscription_preference || []).map((preference) => ({
              value: preference,
              label: preference,
            }))}
            value={nodeDataFormState.subscription_preference || null}
            onChange={(preference) => updateNodeDataFormState({ subscription_preference: preference })}
          />
          <div style={hygieneContainerStyle}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={sectionHeaderStyle}>
                <ShieldTick width={16} height={16} aria-hidden="true" style={{ color: "var(--sl-text-success)" }} />
                <div style={{ ...fieldLabelStyle, fontWeight: 600 }}>Email List Hygiene</div>
              </div>
              <div style={{ ...helpTextStyle, fontSize: 12, lineHeight: "18px" }}>
                Improve deliverability by filtering out disengaged recipients
              </div>
            </div>
            <Checkbox
              label={(
                <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span>Target engaged audience only</span>
                  <span style={{ ...helpTextStyle, fontSize: 12, lineHeight: "18px" }}>
                    Exclude fans who were sent emails in the last 3 months but haven't opened any
                  </span>
                </span>
              )}
              checked={nodeDataFormState.target_engaged_audience || false}
              onChange={(e) => updateNodeDataFormState({ target_engaged_audience: e.target.checked })}
            />
          </div>
        </>
      ) : null}
      <SegmentAudienceWarningModal
        pendingSegment={pendingSegment}
        channel="email"
        onCancel={() => setPendingSegment(null)}
        onConfirm={(segment) => {
          updateNodeDataFormState({ segment_id: segment.segmentId, segment_name: "", segment_label: segment.label });
          setPendingSegment(null);
        }}
      />
    </div>
  );
};

EmailNodeForm.propTypes = {};

export default EmailNodeForm;
